import React, { useEffect } from 'react';
import { mixed, object } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { ChooseOfferFormData, OfferType } from '@jwp/ott-common/types/checkout';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useOffers from '@jwp/ott-hooks-react/src/useOffers';
import useCustomOffers from '@jwp/ott-hooks-react/src/useCustomOffers';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import ChooseOfferCleengForm from '@jwp/ott-ui-react/src/components/ChooseOfferForm/ChooseOfferCleengForm';

import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import useQueryParam from '../../../hooks/useQueryParam';
import ChooseOfferForm from '@jwp/ott-ui-react/src/components/ChooseOfferForm/ChooseOfferForm';

const ChooseOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation('account');
  const isSwitch = useQueryParam('u') === 'upgrade-subscription';
  const isPendingOffer = useAccountStore(({ pendingOffer }) => ({ isPendingOffer: !!pendingOffer }));
  const mediaOffersList = useAccountStore(({ mediaOffer }) => mediaOffer);
  const { isSubscriptionEnabled, isCleeng, isInplayerSubscriptionEnabled, isCleengSubscriptionEnabled } = useAppConfig();

  const { isLoading, mediaOffers, subscriptionOffers, switchSubscriptionOffers, defaultOfferType, hasMultipleOfferTypes, chooseOffer, switchSubscription } =
    useOffers();

  const { offers, cleengOfferLoading } = useCustomOffers();
  const checkoutUrl = modalURLFromLocation(location, 'checkout');
  const upgradePendingUrl = modalURLFromLocation(location, 'upgrade-subscription-pending');
  const upgradeSuccessUrl = modalURLFromLocation(location, 'upgrade-subscription-success');
  const upgradeErrorUrl = modalURLFromLocation(location, 'upgrade-subscription-error');

  const { values, errors, submitting, setValue, handleSubmit, handleChange } = useForm<ChooseOfferFormData>({
    initialValues: { selectedOfferType: defaultOfferType, selectedOfferId: undefined },
    validationSchema: object().shape({
      selectedOfferId: mixed<string>().required(t('choose_offer.field_required')),
      selectedOfferType: mixed<OfferType>().required(t('choose_offer.field_required')),
    }),
    onSubmit: async ({ selectedOfferType, selectedOfferId }) => {
      if (!selectedOfferType || !selectedOfferId) { return; }

      const offer = visibleOffers.find((offer) => offer.offerId === selectedOfferId);

      if (!offer) { return; }

      await chooseOffer.mutateAsync(offer);

      if (isSwitch) {
        return switchSubscription.mutateAsync();
      }
    },
    onSubmitSuccess: async () => {
      if (isSwitch && isPendingOffer) { return navigate(upgradePendingUrl); }
      if (isSwitch) { return navigate(upgradeSuccessUrl); }

      navigate(checkoutUrl);
    },
    onSubmitError: () => navigate(upgradeErrorUrl),
  });

  const visibleOffers = values.selectedOfferType === 'tvod' ? mediaOffers : isSwitch ? switchSubscriptionOffers : subscriptionOffers;

  const data = mediaOffersList?.length ? mediaOffersList?.map((e: any) => e.offerId) : [];

  const result = offers.filter((offer: any) => data.includes(offer.longId));

  useEffect(() => {
    if (isLoading) { return; }
    const hasFirstcleengOffer = result.length > 0 ? !!result[0]?.id : !!offers[0]?.id;

    let offerId: string | undefined;

    if (isInplayerSubscriptionEnabled && visibleOffers[0]?.offerId) {
      offerId = visibleOffers[0]?.offerId;
    } else if (isCleengSubscriptionEnabled && hasFirstcleengOffer) {
      offerId = result.length > 0 ? result[0]?.id : offers[0]?.id;
    } else {
      return;
    }

    if (!values.selectedOfferId && offerId) {
      setValue('selectedOfferId', offerId);
    }
  }, [
    isLoading,
    location.pathname,
    offers,
    result,
    visibleOffers,
    isInplayerSubscriptionEnabled,
    isCleengSubscriptionEnabled,
    values.selectedOfferId,
    setValue,
  ]);

  useEffect(() => {
    if (isLoading || !defaultOfferType) { return; }

    setValue('selectedOfferType', defaultOfferType);
  }, [isLoading, defaultOfferType, setValue]);

  useEffect(() => {
    if (location.pathname.includes('payments')) {
      useAccountStore.setState({ mediaOffer: null });
    }
    if (!isSubscriptionEnabled) {
      navigate(modalURLFromLocation(location, null), { replace: true });
    }
  }, []);

  // loading state
  if (isLoading || cleengOfferLoading) {
    return (
      <div style={{ height: 300 }}>
        <LoadingOverlay inline />
      </div>
    );
  }
  if (isCleengSubscriptionEnabled) {
    return (
      <ChooseOfferCleengForm
        onSubmit={handleSubmit}
        onChange={handleChange}
        values={values}
        errors={errors}
        submitting={submitting}
        offers={result.length > 0 ? result : offers}
        showOfferTypeSwitch={hasMultipleOfferTypes}
      />
    );
  }
  if (isInplayerSubscriptionEnabled) {
    return (
      <ChooseOfferForm
        onSubmit={handleSubmit}
        onChange={handleChange}
        values={values}
        errors={errors}
        submitting={submitting}
        offers={visibleOffers}
        showOfferTypeSwitch={hasMultipleOfferTypes}
      />
    );
  }
  return null;
};

export default ChooseOffer;
